import { OktaAuth } from '@okta/okta-auth-js';
import user from '../store/modules/user';

const isLocal = window.location.href.indexOf('localhost') >= 0;
const isStaging = window.location.href.indexOf('staging') >= 0;
const isDev = window.location.href.indexOf('dev') >= 0;

// 1. Clean Configuration
// We rely on autoRenew: true to handle the heavy lifting
const oktaAuth = new OktaAuth({
    issuer: "https://codeway.okta.com/oauth2/ausy0k6himo2vOpZL697", // Keep your value
    clientId: "0oay0kh7vdYxU9Mv0697", // Keep your value
    redirectUri: window.location.origin + '/login/callback',
    postLogoutRedirectUri: isLocal ? "http://localhost:5002" : isDev ? "https://config-panel.dev.cdwapi.com/" : isStaging ? "https://config-panel.staging.cdwapi.com/" : "https://config-panel.cdwapi.com/",
    scopes: ['openid', 'profile', 'email', 'groups', 'offline_access'],
    responseType: 'code',
    pkce: true,
    tokenManager: {
        storage: 'localStorage',
        autoRenew: false, // SDK handles the timer and renewal logic
        autoRemove: false, // clear token if renewal fails
        secure: !isLocal,
        expireEarlySeconds: 120, // Renew 2 minutes before expiry (standard best practice)
    },
    services: {
        autoRenew: false
    },
    ...(isLocal && {
        disableHttpsCheck: true,
    }),
});

/**
 * Optimized Token Manager Service
 */
const tokenManagerService = {
    _idleInterval: null,
    _lastActivity: Date.now(),
    _maxIdleTime: 8 * 60 * 60 * 1000, // 8 hours
    _isHandlingAuthFailure: false, // Prevent multiple simultaneous redirects
    _tokenRenewalTimeout: null, // Timeout for token renewal listener

    /**
     * Initialize the service
     */
    async initialize() {
        try {
            // This starts the internal Okta timer for auto-renewal
            oktaAuth.tokenManager.start();
            
            // Listen to token expiration and error events
            this._setupTokenEventListeners();
            
            // Start token expiration listener (triggers 2 mins before expiry)
            this._startTokenExpirationListener();
            
            // Start idle monitoring (separate from token logic)
            this._startIdleMonitoring();
            
            console.log('✅ Auth Service initialized');
        } catch (error) {
            console.error('❌ Auth Service init failed:', error);
        }
    },

    /**
     * Setup event listeners for token expiration and errors
     * 
     * IMPORTANT: With autoRenew: true and expireEarlySeconds: 120, Okta should renew tokens
     * 2 minutes BEFORE they expire. If renewal succeeds, the 'expired' event should NOT fire.
     * 
     * The 'expired' event only fires if:
     * 1. Renewal failed (refresh token expired/invalid) - 'error' event will also fire
     * 2. Very short access token TTL where renewal didn't complete in time
     * 3. Network issues preventing renewal
     * 
     * Strategy:
     * - Don't redirect on 'expired' event alone - it's informational
     * - Only redirect on 'error' event with invalid_grant/login_required (actual renewal failure)
     * - Let 401 responses handle edge cases where tokens are expired but no event fired
     */
    _setupTokenEventListeners() {
        // Listen for successful token renewals (for debugging)
        oktaAuth.tokenManager.on('renewed', (key, renewedToken) => {
            console.log(`✅ Token renewed successfully: ${key}`);
        });

        // Listen for expired tokens - informational only
        // With autoRenew: true, this shouldn't fire if renewal succeeded
        // If it does fire, it means renewal didn't happen, but we don't redirect immediately
        // because the 'error' event will fire if renewal actually failed
        oktaAuth.tokenManager.on('expired', (key, expiredToken) => {
            console.warn(`🔐 Token expired: ${key}`);
            console.warn('⚠️ This should not happen if autoRenew is working correctly.');
            console.warn('⚠️ Possible causes: refresh token expired, very short access token TTL, or network issues');
            // Don't redirect here - wait for 'error' event or let 401 handling deal with it
            // This allows Okta's autoRenew to complete if it's still in progress
        });

        // Listen for token renewal errors - THIS is the signal that renewal failed
        // This fires when autoRenew attempts renewal but fails
        oktaAuth.tokenManager.on('error', (error) => {
            console.error('❌ Token manager error:', error);
            
            // Only redirect on actual auth failures that require re-login:
            // - invalid_grant: refresh token expired/invalid
            // - login_required: user needs to re-authenticate
            if (error && (error.error === 'invalid_grant' || error.error === 'login_required')) {
                console.log('🔒 Refresh token invalid - redirecting to login');
                console.log('💡 This means the refresh token has expired or is invalid.');
                console.log('💡 Check your Okta refresh token TTL settings.');
                this.handleAuthFailure('Token renewal failed - refresh token invalid');
            } else {
                // Other errors (network issues, etc.) are transient
                // Don't redirect - let the next API call handle it or retry renewal
                console.warn('⚠️ Token manager error (non-fatal):', error.error || error.message);
                console.warn('⚠️ This might be a transient error - will retry on next request');
            }
        });
    },

    _startTokenExpirationListener() {
        this._stopTokenExpirationListener();
        this._scheduleTokenRenewal();
    },

    _stopTokenExpirationListener() {
        if (this._tokenRenewalTimeout) {
            clearTimeout(this._tokenRenewalTimeout);
            this._tokenRenewalTimeout = null;
        }
    },

    async _scheduleTokenRenewal() {
        try {
            const accessToken = await oktaAuth.tokenManager.get('accessToken');
            const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
            
            if (!accessToken?.expiresAt) {
                this._tokenRenewalTimeout = setTimeout(() => this._scheduleTokenRenewal(), 60000);
                return;
            }

            const now = Math.floor(Date.now() / 1000);
            
            // Check refresh token expiration first - if it expires before access token, we need to handle it
            if (refreshToken?.expiresAt && refreshToken.expiresAt < accessToken.expiresAt) {
                const refreshRenewalTime = refreshToken.expiresAt - now - 120;
                if (refreshRenewalTime <= 0) {
                    await this._renewTokenBeforeExpiry();
                    return;
                }
                // Schedule renewal based on refresh token expiration if it's sooner
                const accessRenewalTime = accessToken.expiresAt - now - 120;
                const renewalTime = Math.min(refreshRenewalTime, accessRenewalTime);
                this._tokenRenewalTimeout = setTimeout(() => this._renewTokenBeforeExpiry(), renewalTime * 1000);
                return;
            }

            // Normal case: schedule based on access token expiration
            const renewalTime = accessToken.expiresAt - now - 120;
            if (renewalTime <= 0) {
                await this._renewTokenBeforeExpiry();
            } else {
                this._tokenRenewalTimeout = setTimeout(() => this._renewTokenBeforeExpiry(), renewalTime * 1000);
            }
        } catch (error) {
            console.error('❌ Error scheduling token renewal:', error);
            this._tokenRenewalTimeout = setTimeout(() => this._scheduleTokenRenewal(), 60000);
        }
    },

    async _renewTokenBeforeExpiry() {
        const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
        const now = Math.floor(Date.now() / 1000);

        if (!refreshToken || refreshToken.expiresAt <= now || refreshToken.expiresAt < (now + 120)) {
            this.handleAuthFailure('Token renewal failed - refresh token expired');
            return;
        }

        try {
            await oktaAuth.tokenManager.renew('accessToken');
            this.updateActivity();
            await user.dispatch('setUser');
            this._scheduleTokenRenewal();
        } catch (error) {
            if (error?.error === 'invalid_grant' || error?.error === 'login_required') {
                this.handleAuthFailure('Token renewal failed - refresh token invalid');
            } else {
                this._scheduleTokenRenewal();
            }
        }
    },

    /**
     * Centralized handler for authentication failures
     * Redirects to login and saves current path for post-login navigation
     */
    handleAuthFailure(reason = 'Authentication required') {
        // Prevent multiple simultaneous redirects
        if (this._isHandlingAuthFailure) {
            return;
        }
        this._isHandlingAuthFailure = true;

        console.log(`🔒 Auth failure: ${reason}. Redirecting to login...`);

        // Save current path for post-login redirect (if not already saved)
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && !['/signin', '/login/callback', '/auth/processing'].includes(currentPath)) {
            if (!sessionStorage.getItem('okta_redirect_path')) {
                sessionStorage.setItem('okta_redirect_path', currentPath);
            }
        }
        debugger;
        // Clear token cache
        this.clearTokenCache();

        // Stop idle monitoring
        this._stopIdleMonitoring();

        // Stop token expiration listener
        this._stopTokenExpirationListener();

        // Redirect to signin page
        if (window.location.pathname !== '/signin') {
            window.location.href = '/signin';
        } else {
            // If already on signin, reset the flag after a delay
            setTimeout(() => {
                this._isHandlingAuthFailure = false;
            }, 1000);
        }
    },

    /**
     * Clear token cache (called on logout and auth failures)
     */
    clearTokenCache() {
        // Clear any cached tokens
        try {
            oktaAuth.tokenManager.clear();
        } catch (error) {
            console.warn('Error clearing token cache:', error);
        }
    },

    /**
     * Get Access Token
     * Returns string or null.
     * * The SDK's getAccessToken() does the following automatically:
     * 1. Checks memory/storage for token.
     * 2. If valid -> returns it.
     * 3. If expiring soon (based on expireEarlySeconds) -> performs refresh -> returns new token.
     * 4. If expired/invalid -> returns undefined.
     * 
     * Note: We rely on Okta's autoRenew to handle renewal. Event listeners will catch failures.
     */
    /**
     * Get Access Token (Force Check Expiration)
     */
    async getValidToken() {
        try{
            const token = await oktaAuth.tokenManager.get('accessToken');
            if(token){
                this.updateActivity();
                return token.accessToken;
            }
        } catch (error) {
            console.error('❌ Error getting valid token:', error);
            this.handleAuthFailure( 'Token renewal failed - access token invalid');
        } 
        return null;
    },

    /**
     * Get ID Token
     * Note: We rely on Okta's autoRenew to handle renewal. Event listeners will catch failures.
     */
    async getValidIdToken() {
        try {
            const token = await oktaAuth.getIdToken();
            if (token) this.updateActivity();
            return token.idToken;
        } catch (error) {
            console.error('❌ Error retrieving ID token:', error);
            // Let event listeners handle auth failures - they'll trigger redirect
            return null;
        }
    },

    async _refreshTokenIfNeeded() {
        let accessToken = await oktaAuth.tokenManager.get('accessToken');
        let refreshToken = await oktaAuth.tokenManager.get('refreshToken');
        const now = Math.floor(Date.now() / 1000);
        debugger;
        if(accessToken && accessToken.expiresAt < (now + 120)) {
            debugger;
            if(refreshToken && refreshToken.expiresAt > now + 120) {
                try{
                 await oktaAuth.tokenManager.renew('accessToken');
                 this.updateActivity();
                 await user.dispatch('setUser');
                } catch (error) {
                    console.error('❌ Renewal failed: Unexpected error', JSON.stringify(error));
                    this.handleAuthFailure( 'Token renewal failed - access token invalid');
                }
            }
            else{
                console.error('❌ Renewal failed: Refresh token expired');
                this.handleAuthFailure( 'Token renewal failed - refresh token invalid');
            }
        }
        return
    },

    /**
     * Force Login
     */
    async login(fromPath = null) {
        if (fromPath) {
            sessionStorage.setItem('okta_redirect_path', fromPath);
        }
        await oktaAuth.signInWithRedirect();
    },

    /**
     * Logout
     */
    async logout() {
        console.log('👋 Logging out...');
        this._stopIdleMonitoring();
        this._stopTokenExpirationListener();
        
        // This handles local storage clearing AND redirecting to Okta to kill the session
        await oktaAuth.signOut({ 
            postLogoutRedirectUri: this._getPostLogoutRedirectUri() 
        });
    },

    /**
     * Handle the Callback (Use in your /login/callback route)
     */
    async handleLoginCallback() {
        try {
            await oktaAuth.handleLoginRedirect();
            
            // Restart token expiration listener for new tokens
            this._startTokenExpirationListener();
            
            // Redirect back to where user was, or home
            const savedPath = sessionStorage.getItem('okta_redirect_path');
            sessionStorage.removeItem('okta_redirect_path');
            return savedPath || '/';
        } catch (error) {
            console.error('❌ Callback error:', error);
            throw error;
        }
    },

    async getUserInfo() {
        const accessTokenObj = await oktaAuth.tokenManager.get('accessToken');
        if (accessTokenObj) {
            return await oktaAuth.token.getUserInfo(accessTokenObj);
        }
        return null;
    },

    // ==================== IDLE MANAGEMENT ====================
    // Kept simple and separate from token logic
    
    updateActivity() {
        this._lastActivity = Date.now();
    },

    _startIdleMonitoring() {
        // Track basic interactions
        ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(evt => 
            window.addEventListener(evt, () => this.updateActivity(), { passive: true })
        );

        // Check every minute
        this._idleInterval = setInterval(() => {
            if (Date.now() - this._lastActivity > this._maxIdleTime) {
                console.log('💤 Idle timeout reached');
                this.logout();
            }
        }, 60000);
    },

    _stopIdleMonitoring() {
        if (this._idleInterval) clearInterval(this._idleInterval);
    },

    _getPostLogoutRedirectUri() {
         return isLocal ? "http://localhost:5002" : 
               isDev ? "https://config-panel.dev.cdwapi.com/" : 
               isStaging ? "https://config-panel.staging.cdwapi.com/" : 
               "https://config-panel.cdwapi.com/";
    }
};

export default tokenManagerService;
export { oktaAuth };